import React, { useState, useEffect, useRef, useCallback, FC } from "react";
import VideoSourceDropdown from "../components/VideoSourceDropdown"; // Import the new component
import "./InstaKeys.scss";
import AudioSourceDropdown from "../components/AudioSourceDropdown";
import PianoCanvas from "../core/PianoCanvas";
import ColorPicker from "../components/ColorPicker";
import OctaveOffsetButtons from "../core/OctaveOffsetButtons";
import PianoPositionControl from "../core/PianoPositionControl";
import { inject } from "@vercel/analytics";
import OctaveRangeDropdown from "../components/SelectOctaveRangeDropdown";
import { OctaveOptions } from "../models/customTypes";
import { MidiProvider, useMidi } from "../contexts/MidiContext";
import { FFmpeg } from "@ffmpeg/ffmpeg";
import { fetchFile, toBlobURL } from "@ffmpeg/util";
import { useNavigate } from "react-router-dom";
import {
  PianoConfig,
  MASTER_CANVAS_HEIGHT,
  MASTER_CANVAS_WIDTH,
  computeMasterCanvasLayout,
  getWhiteKeyDimensions,
} from "../helpers/pianoLayoutHelpers";
import {
  getRecordingErrorMessage,
  buildMp4ConversionArgs,
  normalizeFfmpegProgress,
} from "../helpers/recordingHelpers";
import type { NoteEvent } from "../helpers/recordingHelpers";
import { createFrameLoop } from "./frameLoop";
import { createRecordingSession } from "./recordingSession";
import { renderEffectsVideo } from "./effectsExport";

inject();

/**
 * `useRef(create())` evaluates `create()` on every render and throws the result
 * away. These factories allocate loops and recorders, so build them once.
 */
function useLazyRef<T>(create: () => T): React.MutableRefObject<T> {
  const ref = useRef<T | null>(null);
  if (ref.current === null) ref.current = create();
  return ref as React.MutableRefObject<T>;
}

/* ── MidiEventRecorder: lives inside MidiProvider, watches noteHash ──────── */

const MidiEventRecorder: FC<{
  isRecording: boolean;
  onNoteEvent: (note: number, type: 'on' | 'off', timeMs: number) => void;
  startTimeRef: React.MutableRefObject<number>;
}> = ({ isRecording, onNoteEvent, startTimeRef }) => {
  const { noteHash } = useMidi();
  const prevNoteHashRef = useRef<Record<number, boolean>>({});

  useEffect(() => {
    if (!isRecording) {
      prevNoteHashRef.current = {};
      return;
    }
    const prev = prevNoteHashRef.current;
    const timeMs = performance.now() - startTimeRef.current;
    const allNotes = new Set([
      ...Object.keys(prev).map(Number),
      ...Object.keys(noteHash).map(Number),
    ]);
    allNotes.forEach((note) => {
      const wasOn = !!prev[note];
      const isOn = !!noteHash[note];
      if (wasOn !== isOn) {
        onNoteEvent(note, isOn ? 'on' : 'off', timeMs);
      }
    });
    prevNoteHashRef.current = { ...noteHash };
  }, [noteHash, isRecording]); // startTimeRef is a ref — stable, no need in deps

  return null;
};

// Component to display MIDI status and enable button (must be inside MidiProvider)
const MidiStatusControl: FC = () => {
  const { midiAccess, requestMIDIAccess, isMIDISupported } = useMidi();
  const [isRequesting, setIsRequesting] = useState(false);

  const handleEnableMIDI = async () => {
    setIsRequesting(true);
    try {
      await requestMIDIAccess();
    } catch (error) {
      console.error("Failed to enable MIDI:", error);
    } finally {
      setIsRequesting(false);
    }
  };

  if (!isMIDISupported) {
    return (
      <div
        style={{
          marginTop: "10px",
          padding: "10px",
          backgroundColor: "#ffebee",
          borderRadius: "4px",
          fontSize: "12px",
          color: "#c62828",
        }}
      >
        ⚠️ WebMIDI is not supported in this browser. Please use Chrome or Safari
        on iPad.
      </div>
    );
  }

  if (!midiAccess) {
    return (
      <div style={{ marginTop: "10px" }}>
        <button
          onClick={handleEnableMIDI}
          disabled={isRequesting}
          style={{
            backgroundColor: isRequesting ? "#95a5a6" : "#3498db",
            color: "white",
            border: "none",
            padding: "8px 16px",
            borderRadius: "4px",
            cursor: isRequesting ? "not-allowed" : "pointer",
            fontSize: "14px",
            fontWeight: "500",
            width: "100%",
          }}
        >
          {isRequesting ? "Enabling MIDI..." : "Enable MIDI"}
        </button>
        <div style={{ marginTop: "5px", fontSize: "12px", color: "#d7e8f4" }}>
          Click to enable MIDI input for your keyboard
        </div>
      </div>
    );
  }

  const inputCount = midiAccess.inputs.size;
  return (
    <div
      style={{
        marginTop: "10px",
        padding: "8px",
        backgroundColor: "#e8f5e9",
        borderRadius: "4px",
        fontSize: "12px",
        color: "#2e7d32",
      }}
    >
      ✓ MIDI Enabled ({inputCount} input{inputCount !== 1 ? "s" : ""} detected)
    </div>
  );
};

const InstaKeys: FC = () => {
  const navigate = useNavigate();

  /* Master Canvas stuff */
  const DEFAULT_OCTAVE_OFFSET = 0;
  const masterCanvasRef = useRef<HTMLCanvasElement | null>(null);

  /* Video stuff */
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const [recording, setRecording] = useState<boolean>(false);
  const [recordedChunks, setRecordedChunks] = useState<BlobPart[]>([]);
  const [hasMediaPermission, setHasMediaPermission] = useState<boolean>(false);
  const masterCanvasStreamRef = useRef<MediaStream | null>(null);
  /* Wall-clock length of the last take — a MediaRecorder WebM has no duration
     in its header, so the effects pass would otherwise have nothing to scale
     its progress against. */
  const recordedDurationMsRef = useRef<number>(0);

  /* Piano stuff */
  const pianoRef = useRef<HTMLCanvasElement | null>(null);
  const [userPianoHeight, setUserPianoHeight] = useState<number>(-575);
  const [pressedKeyColor, setPressedKeyColor] = useState("#00FF00"); // default to Bright Neon Green
  const [octaveOffset, setOctaveOffset] = useState<number>(
    DEFAULT_OCTAVE_OFFSET,
  );
  const [octaveRange, setOctaveRange] = useState<OctaveOptions>(6);

  /* Backing track stuff */
  const [backingTrackFile, setBackingTrackFile] = useState<File | null>(null);
  const [backingTrackVolume, setBackingTrackVolume] = useState<number>(0.8);
  const audioContextRef = useRef<AudioContext | null>(null);
  const backingTrackAudioRef = useRef<HTMLAudioElement | null>(null);
  const backingTrackGainRef = useRef<GainNode | null>(null);
  const backingTrackObjectUrlRef = useRef<string | null>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const waveformCanvasRef = useRef<HTMLCanvasElement | null>(null);

  /* Intro image stuff — displayed for the first 1.5s of recording, fading out */
  const [introImageFile, setIntroImageFile] = useState<File | null>(null);
  const [introImagePreviewUrl, setIntroImagePreviewUrl] = useState<string | null>(null);
  const introImageElementRef = useRef<HTMLImageElement | null>(null);
  const introImageUrlRef = useRef<string | null>(null);   // tracked for cleanup
  const recordingStartTimeRef = useRef<number>(0);

  /* Effects rendering stuff */
  const noteEventsRef = useRef<NoteEvent[]>([]);
  const pianoConfigRef = useRef<PianoConfig>({
    whiteKeyWidth: 25.2,
    whiteKeyHeight: 112,
    numOctaves: 6,
    octaveOffset: 1,
    userPianoHeight: -575,
    pressedKeyColor: '#00FF00',
  });
  const [renderingEffects, setRenderingEffects] = useState<boolean>(false);
  const [effectsProgress, setEffectsProgress] = useState<number>(0);

  /* Piano Settings */
  const [chordsEnabled, setChordsEnabled] = useState<boolean>(false);
  const [bubbleChords, setBubbleChords] = useState<boolean>(false);

  /* Settings stuff */
  const [videoDevices, setVideoDevices] = useState<MediaDeviceInfo[]>([]);
  const [audioDevices, setAudioDevices] = useState<MediaDeviceInfo[]>([]);
  const [selectedVideoDevice, setSelectedVideoDevice] = useState<string | null>(
    null,
  );
  const [selectedAudioDevice, setSelectedAudioDevice] = useState<string | null>(
    null,
  );

  /* FFmpeg stuff */
  const ffmpegRef = useRef<FFmpeg>(new FFmpeg());
  const [ffmpegLoaded, setFfmpegLoaded] = useState<boolean>(false);
  const [converting, setConverting] = useState<boolean>(false);
  const [conversionProgress, setConversionProgress] = useState<number>(0);

  const RECORDING_FPS = 30;
  const RECORDING_VIDEO_BITRATE = 8000000;
  const EXPORT_MAX_WIDTH = 1280;
  const EXPORT_FPS = 30;
  const INTRO_FADE_SECONDS = 1.5;

  /* ── Loop and recorder ───────────────────────────────────────────────────
     Both are refs holding a single long-lived instance. The frame loop reads
     mutable refs rather than captured state, so it can never paint from a
     stale closure, and it keeps running through a transient draw failure or a
     backgrounded tab — either of which would otherwise freeze the capture
     stream mid-take while audio kept recording. */

  /* Latest painters and values the long-lived loops read, so they always
     draw what is on screen right now instead of a captured snapshot. */
  const drawMasterCanvasFrameRef = useRef<() => void>(() => {});
  const drawWaveformFrameRef = useRef<() => void>(() => {});
  const userPianoHeightRef = useRef<number>(userPianoHeight);

  const frameLoopRef = useLazyRef(() =>
    createFrameLoop({
      onFrame: () => drawMasterCanvasFrameRef.current(),
      onError: (error, consecutiveErrors) => {
        // Noisy only on the first failure and once a second after that.
        if (consecutiveErrors === 1 || consecutiveErrors % RECORDING_FPS === 0) {
          console.warn(
            `Master canvas frame failed (${consecutiveErrors} in a row):`,
            error,
          );
        }
      },
      hiddenFps: RECORDING_FPS,
    }),
  );

  const waveformLoopRef = useLazyRef(() =>
    createFrameLoop({ onFrame: () => drawWaveformFrameRef.current() }),
  );

  const recordingSessionRef = useLazyRef(() =>
    createRecordingSession({
      createRecorder: (stream, options) => new MediaRecorder(stream, options),
      isTypeSupported: (mimeType) => MediaRecorder.isTypeSupported(mimeType),
    }),
  );

  /* ── MIDI event collector (called by MidiEventRecorder) ─────────────────── */

  const handleNoteEvent = useCallback(
    (note: number, type: "on" | "off", timeMs: number) => {
      noteEventsRef.current.push({ note, type, timeMs });
    },
    [],
  );

  /* ── Download with Effects ───────────────────────────────────────────────── */

  const triggerDownload = (blob: Blob, filename: string) => {
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.style.display = "none";
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    // Revoking synchronously can cancel the download in some browsers.
    window.setTimeout(() => URL.revokeObjectURL(url), 10_000);
  };

  const convertWebmToMp4 = async (webmBlob: Blob): Promise<Blob> => {
    const ffmpeg = ffmpegRef.current;
    const input = `input-${Date.now()}.webm`;
    const output = `output-${Date.now()}.mp4`;

    try {
      await ffmpeg.writeFile(input, await fetchFile(webmBlob));
      await ffmpeg.exec(
        buildMp4ConversionArgs(input, output, EXPORT_MAX_WIDTH, EXPORT_FPS),
      );
      const data = await ffmpeg.readFile(output);
      return new Blob([data as BlobPart], { type: "video/mp4" });
    } finally {
      // Always clear the virtual filesystem, or a failed conversion keeps the
      // whole clip resident in wasm memory until the page reloads.
      await ffmpeg.deleteFile(input).catch(() => {});
      await ffmpeg.deleteFile(output).catch(() => {});
    }
  };

  const downloadWithEffects = async (format: "webm" | "mp4" = "webm") => {
    if (recordedChunks.length === 0 || renderingEffects) return;
    if (format === "mp4" && !ffmpegLoaded) {
      alert("Video converter is still loading. Please try again in a moment.");
      return;
    }

    setRenderingEffects(true);
    setEffectsProgress(0);

    try {
      const effectsBlob = await renderEffectsVideo({
        recordedChunks,
        recordedDurationMs: recordedDurationMsRef.current,
        noteEvents: noteEventsRef.current,
        config: pianoConfigRef.current,
        fps: RECORDING_FPS,
        videoBitsPerSecond: RECORDING_VIDEO_BITRATE,
        onProgress: setEffectsProgress,
      });

      if (format === "mp4") {
        triggerDownload(await convertWebmToMp4(effectsBlob), "instakeys-effects.mp4");
      } else {
        triggerDownload(effectsBlob, "instakeys-effects.webm");
      }
    } catch (error) {
      console.error("Effects render failed:", error);
      alert("Failed to render effects video. Please try again.");
    } finally {
      setRenderingEffects(false);
      setEffectsProgress(0);
    }
  };

  const drawWaveformFrame = () => {
    const analyser = analyserRef.current;
    const canvas = waveformCanvasRef.current;
    if (!analyser || !canvas) return;

    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const bufferLength = analyser.frequencyBinCount;
    const dataArray = new Uint8Array(bufferLength);
    analyser.getByteTimeDomainData(dataArray);

    const { width, height } = canvas;
    ctx.clearRect(0, 0, width, height);

    // Subtle background
    ctx.fillStyle = "rgba(0,0,0,0.15)";
    ctx.fillRect(0, 0, width, height);

    // Waveform line
    ctx.lineWidth = 2;
    ctx.strokeStyle = "#f0a76b";
    ctx.shadowColor = "rgba(240,167,107,0.5)";
    ctx.shadowBlur = 4;
    ctx.beginPath();

    const sliceWidth = width / bufferLength;
    let x = 0;
    for (let i = 0; i < bufferLength; i++) {
      const v = dataArray[i] / 128.0;
      const y = (v * height) / 2;
      i === 0 ? ctx.moveTo(x, y) : ctx.lineTo(x, y);
      x += sliceWidth;
    }
    ctx.lineTo(width, height / 2);
    ctx.stroke();
    ctx.shadowBlur = 0;
  };

  const startWaveform = () => waveformLoopRef.current.start();

  const stopWaveform = () => {
    waveformLoopRef.current.stop();
    const canvas = waveformCanvasRef.current;
    const ctx = canvas?.getContext("2d");
    if (canvas && ctx) ctx.clearRect(0, 0, canvas.width, canvas.height);
  };

  /* ── Audio graph ─────────────────────────────────────────────────────────
     Mixes the mic with the optional backing track into a single track for the
     recorder. Returns a teardown that the recording session runs once the
     recorder has finished flushing. */

  const attachAudio = (
    targetStream: MediaStream,
    micTrack: MediaStreamTrack,
  ): (() => void) => {
    if (!backingTrackFile) {
      // No backing track — record the raw mic track directly.
      targetStream.addTrack(micTrack);
      return () => targetStream.removeTrack(micTrack);
    }

    const audioContext = new AudioContext();
    audioContextRef.current = audioContext;

    // Mic goes to the recording only, never to the speakers, to avoid feedback.
    const micGain = audioContext.createGain();
    micGain.gain.value = 1.0;
    audioContext.createMediaStreamSource(new MediaStream([micTrack])).connect(micGain);

    const objectUrl = URL.createObjectURL(backingTrackFile);
    backingTrackObjectUrlRef.current = objectUrl;
    const backingAudio = new Audio(objectUrl);
    backingAudio.loop = false;
    backingTrackAudioRef.current = backingAudio;

    const backingGain = audioContext.createGain();
    backingGain.gain.value = backingTrackVolume;
    backingTrackGainRef.current = backingGain;
    audioContext.createMediaElementSource(backingAudio).connect(backingGain);

    // The analyser both feeds the waveform display and passes audio through.
    const analyser = audioContext.createAnalyser();
    analyser.fftSize = 512;
    analyserRef.current = analyser;
    backingGain.connect(analyser);

    const destination = audioContext.createMediaStreamDestination();
    micGain.connect(destination);
    analyser.connect(destination);
    analyser.connect(audioContext.destination); // so the player hears the track

    const mixedTrack = destination.stream.getAudioTracks()[0];
    if (mixedTrack) targetStream.addTrack(mixedTrack);

    backingAudio.currentTime = 0;
    backingAudio
      .play()
      .catch((err) => console.error("Error playing backing track:", err));
    startWaveform();

    return () => {
      stopWaveform();
      backingAudio.pause();
      backingAudio.removeAttribute("src");
      backingAudio.load();
      if (mixedTrack) mixedTrack.stop();
      if (audioContext.state !== "closed") void audioContext.close();
      URL.revokeObjectURL(objectUrl);

      backingTrackAudioRef.current = null;
      backingTrackObjectUrlRef.current = null;
      backingTrackGainRef.current = null;
      analyserRef.current = null;
      audioContextRef.current = null;
    };
  };

  /* ── Recording ───────────────────────────────────────────────────────────── */

  const startRecording = () => {
    // The session's state is updated synchronously, unlike `recording`, so a
    // double-click cannot slip a second start through before React re-renders.
    if (recordingSessionRef.current.state !== "idle") return;
    if (converting) {
      alert(
        "Please wait for MP4 conversion to finish before starting a new recording.",
      );
      return;
    }

    const cameraStream = videoRef.current?.srcObject as MediaStream | undefined;
    if (!cameraStream) {
      console.error("Video stream is not available");
      alert(
        "Video stream is not available. Please ensure your camera is connected.",
      );
      return;
    }

    const masterCanvasStream =
      masterCanvasRef.current?.captureStream(RECORDING_FPS);
    if (!masterCanvasStream) {
      console.error("Canvas stream is not available");
      alert("Unable to capture canvas stream. Please try again.");
      return;
    }

    // Reset per-take capture state before anything can write to it.
    setRecordedChunks([]);
    noteEventsRef.current = [];
    recordedDurationMsRef.current = 0;

    // Snapshot the layout so the effects pass renders against the same piano
    // the recording was composited with, even if the user changes it later.
    const { width: whiteKeyWidth, height: whiteKeyHeight } =
      getWhiteKeyDimensions(octaveRange);
    pianoConfigRef.current = {
      whiteKeyWidth,
      whiteKeyHeight,
      numOctaves: octaveRange,
      octaveOffset,
      userPianoHeight,
      pressedKeyColor,
    };

    const micTrack = cameraStream.getAudioTracks()[0];
    let detachAudio: () => void = () => {};
    if (micTrack) {
      detachAudio = attachAudio(masterCanvasStream, micTrack);
    } else {
      console.warn("No audio track found in video stream");
    }

    const releaseCapture = () => {
      detachAudio();
      // Only the canvas capture tracks are ours to stop; the mic track belongs
      // to the live preview stream and must keep running.
      masterCanvasStream.getVideoTracks().forEach((track) => track.stop());
      if (masterCanvasStreamRef.current === masterCanvasStream) {
        masterCanvasStreamRef.current = null;
      }
    };

    masterCanvasStreamRef.current = masterCanvasStream;
    recordingStartTimeRef.current = performance.now();
    frameLoopRef.current.start();

    try {
      recordingSessionRef.current.start({
        stream: masterCanvasStream,
        videoBitsPerSecond: RECORDING_VIDEO_BITRATE,
        timesliceMs: 500,
        onRelease: releaseCapture,
        onError: (error) => {
          const name = (error as DOMException).name;
          console.error("MediaRecorder error:", error);
          if (name) alert(getRecordingErrorMessage(name));
          setRecording(false);
        },
      });
    } catch (error) {
      console.error("Error starting MediaRecorder:", error);
      alert(
        "Unable to start recording. Your browser may not support video recording.",
      );
      frameLoopRef.current.stop();
      releaseCapture();
      return;
    }

    setRecording(true);
  };

  const stopRecording = () => {
    if (recordingSessionRef.current.state !== "recording") return;

    // Stop painting first so no further frames reach the capture stream, then
    // let the session flush; it releases the capture resources on its own once
    // the recorder has handed over its final chunk.
    frameLoopRef.current.stop();
    setRecording(false);

    void recordingSessionRef.current.stop().then((result) => {
      if (!result) return;
      recordedDurationMsRef.current = result.durationMs;
      setRecordedChunks(result.chunks);
    });
  };

  /* ── Master canvas compositor ────────────────────────────────────────────── */

  const drawMasterCanvasFrame = () => {
    const masterCtx = masterCanvasRef.current?.getContext("2d");
    const video = videoRef.current;
    const piano = pianoRef.current;
    if (!masterCtx || !video || !piano) return;

    const layout = computeMasterCanvasLayout({
      videoIntrinsicWidth: video.videoWidth,
      videoIntrinsicHeight: video.videoHeight,
      pianoCanvasHeight: piano.height,
      userPianoHeight: userPianoHeightRef.current,
    });
    // The camera has not produced a sized frame yet — keep the previous frame
    // on screen rather than drawing at NaN coordinates.
    if (!layout) return;

    masterCtx.imageSmoothingEnabled = true;
    masterCtx.imageSmoothingQuality = "high";

    masterCtx.drawImage(
      video,
      layout.video.x,
      layout.video.y,
      layout.video.width,
      layout.video.height,
    );
    masterCtx.drawImage(
      piano,
      layout.piano.x,
      layout.piano.y,
      layout.piano.width,
      layout.piano.height,
    );

    // Intro image — fades out over the first 1.5 seconds of the take.
    const introImage = introImageElementRef.current;
    if (introImage) {
      const elapsed = (performance.now() - recordingStartTimeRef.current) / 1000;
      if (elapsed < INTRO_FADE_SECONDS) {
        masterCtx.globalAlpha = 1 - elapsed / INTRO_FADE_SECONDS;
        masterCtx.drawImage(
          introImage,
          0,
          0,
          MASTER_CANVAS_WIDTH,
          MASTER_CANVAS_HEIGHT,
        );
        masterCtx.globalAlpha = 1.0;
      }
    }
  };

  const downloadWebM = () => {
    if (recordedChunks.length === 0) return;
    triggerDownload(
      new Blob(recordedChunks, { type: "video/webm" }),
      "instakeys-recording.webm",
    );
  };

  const convertToMP4 = async () => {
    if (recordedChunks.length === 0 || converting) return;
    if (!ffmpegLoaded) {
      alert("Video converter is still loading. Please wait a moment and try again.");
      return;
    }

    setConverting(true);
    setConversionProgress(0);

    try {
      const mp4Blob = await convertWebmToMp4(
        new Blob(recordedChunks, { type: "video/webm" }),
      );
      triggerDownload(mp4Blob, "instakeys-recording.mp4");
    } catch (error) {
      console.error("Error converting video:", error);
      alert("Failed to convert video. Please try again.");
    } finally {
      setConverting(false);
      setConversionProgress(0);
    }
  };

  const handleOctaveIncrement = () => {
    setOctaveOffset((prev) => prev + 1);
  };

  const handleOctaveDecrement = () => {
    setOctaveOffset((prev) => prev - 1);
  };

  useEffect(() => {
    handleOctaveIncrement(); // TODO: Remove this after octave rewrite.
  }, []);

  /* Pre-load intro image whenever the selected file changes */
  useEffect(() => {
    // Revoke the previous object URL to avoid memory leaks
    if (introImageUrlRef.current) {
      URL.revokeObjectURL(introImageUrlRef.current);
      introImageUrlRef.current = null;
    }
    introImageElementRef.current = null;
    setIntroImagePreviewUrl(null);

    if (introImageFile) {
      const url = URL.createObjectURL(introImageFile);
      introImageUrlRef.current = url;
      setIntroImagePreviewUrl(url);

      const img = new Image();
      img.onload = () => {
        introImageElementRef.current = img;
      };
      img.src = url;
    }

    return () => {
      // Cleanup on unmount
      if (introImageUrlRef.current) {
        URL.revokeObjectURL(introImageUrlRef.current);
      }
    };
  }, [introImageFile]);

  /* Load FFmpeg on component mount */
  useEffect(() => {
    const loadFFmpeg = async () => {
      const ffmpeg = ffmpegRef.current;

      ffmpeg.on("progress", ({ progress }) => {
        const percent = normalizeFfmpegProgress(progress);
        // A duration-less WebM gives ffmpeg nothing to measure against; leave
        // the UI indeterminate rather than showing an invented number.
        if (percent === null) return;
        // ffmpeg's ratio can jog backwards between packets; only ever advance.
        setConversionProgress((prev) => Math.max(prev, percent));
      });

      try {
        const baseURL = "https://unpkg.com/@ffmpeg/core@0.12.6/dist/umd";
        await ffmpeg.load({
          coreURL: await toBlobURL(
            `${baseURL}/ffmpeg-core.js`,
            "text/javascript",
          ),
          wasmURL: await toBlobURL(
            `${baseURL}/ffmpeg-core.wasm`,
            "application/wasm",
          ),
        });
        setFfmpegLoaded(true);
      } catch (error) {
        console.error("Failed to load FFmpeg:", error);
      }
    };

    loadFFmpeg();
  }, []);

  // Reusable function to enumerate devices
  const enumerateDevices = useCallback(
    async (preserveSelection: boolean = false) => {
      try {
        // If we don't have permission yet, request it first
        if (!hasMediaPermission) {
          const stream = await navigator.mediaDevices.getUserMedia({
            video: true,
            audio: true,
          });
          // Stop the stream immediately since we just want permission
          stream.getTracks().forEach((track) => track.stop());
          setHasMediaPermission(true);
        }

        // Now enumerate devices
        const deviceInfos = await navigator.mediaDevices.enumerateDevices();

        const videoDeviceInfos = deviceInfos.filter(
          (device) => device.kind === "videoinput",
        );
        const audioDeviceInfos = deviceInfos.filter(
          (device) => device.kind === "audioinput",
        );

        setVideoDevices(videoDeviceInfos);
        setAudioDevices(audioDeviceInfos);

        // Preserve current selection if device still exists, otherwise select first available
        if (preserveSelection) {
          setSelectedVideoDevice((currentVideoDevice) => {
            const currentVideoExists = videoDeviceInfos.some(
              (device) => device.deviceId === currentVideoDevice,
            );
            if (!currentVideoExists && videoDeviceInfos.length > 0) {
              return videoDeviceInfos[0].deviceId;
            }
            return currentVideoDevice;
          });

          setSelectedAudioDevice((currentAudioDevice) => {
            const currentAudioExists = audioDeviceInfos.some(
              (device) => device.deviceId === currentAudioDevice,
            );
            if (!currentAudioExists && audioDeviceInfos.length > 0) {
              return audioDeviceInfos[0].deviceId;
            }
            return currentAudioDevice;
          });
        } else {
          // Initial load - select first device
          if (videoDeviceInfos.length > 0) {
            setSelectedVideoDevice(videoDeviceInfos[0].deviceId);
          }
          if (audioDeviceInfos.length > 0) {
            setSelectedAudioDevice(audioDeviceInfos[0].deviceId);
          }
        }
      } catch (err) {
        console.error("Error accessing media devices:", err);
      }
    },
    [hasMediaPermission],
  );

  // Initial device enumeration
  useEffect(() => {
    enumerateDevices(false);
  }, [enumerateDevices]);

  // Listen for device changes (e.g., when iPad is connected)
  useEffect(() => {
    const handleDeviceChange = () => {
      enumerateDevices(true); // Preserve current selection if possible
    };

    navigator.mediaDevices.addEventListener("devicechange", handleDeviceChange);

    return () => {
      navigator.mediaDevices.removeEventListener(
        "devicechange",
        handleDeviceChange,
      );
    };
  }, [enumerateDevices]);

  useEffect(() => {
    if (selectedVideoDevice || selectedAudioDevice) {
      // First, stop any existing tracks to free up the device
      if (videoRef.current?.srcObject) {
        const oldStream = videoRef.current.srcObject as MediaStream;
        oldStream.getTracks().forEach((track) => track.stop());
        // Clear the srcObject to ensure the video element properly updates to the new source
        videoRef.current.srcObject = null;
      }

      const constraints = {
        video: selectedVideoDevice
          ? {
              deviceId: { exact: selectedVideoDevice },
              // Remove facingMode as it conflicts with Continuity Camera
              width: { ideal: 1280 },
              height: { ideal: 720 },
              frameRate: { ideal: 30 },
            }
          : false,
        audio: selectedAudioDevice
          ? {
              deviceId: { exact: selectedAudioDevice },
              noiseSuppression: false,
              echoCancellation: false,
              autoGainControl: false,
              sampleRate: 48000,
              channelCount: 2,
            }
          : false,
      };

      navigator.mediaDevices
        .getUserMedia(constraints)
        .then((stream) => {
          if (videoRef.current) {
            // Ensure iOS-required attributes are set programmatically
            videoRef.current.setAttribute("autoplay", "true");
            videoRef.current.setAttribute("playsinline", "true");
            videoRef.current.setAttribute("muted", "true");
            videoRef.current.autoplay = true;
            videoRef.current.playsInline = true;
            videoRef.current.muted = true;

            videoRef.current.srcObject = stream;

            videoRef.current.onerror = (e) => {
              console.error("Video error:", e);
              console.error("Video error details:", videoRef.current?.error);
            };

            // Try to play the video
            videoRef.current.play().catch((err) => {
              console.error("Error playing video:", err);
            });
          }
        })
        .catch((error) => {
          console.error("Error accessing media devices:", error);
          console.error("Error name:", error.name);
          console.error("Error message:", error.message);

          // Fallback to basic constraints if high quality fails
          const fallbackConstraints = {
            video: selectedVideoDevice
              ? { deviceId: { ideal: selectedVideoDevice } }
              : false,
            audio: selectedAudioDevice
              ? { deviceId: { ideal: selectedAudioDevice } }
              : false,
          };

          return navigator.mediaDevices.getUserMedia(fallbackConstraints);
        })
        .then((fallbackStream) => {
          if (fallbackStream && videoRef.current) {
            // Ensure iOS-required attributes are set programmatically
            videoRef.current.setAttribute("autoplay", "true");
            videoRef.current.setAttribute("playsinline", "true");
            videoRef.current.setAttribute("muted", "true");
            videoRef.current.autoplay = true;
            videoRef.current.playsInline = true;
            videoRef.current.muted = true;

            videoRef.current.srcObject = fallbackStream;

            // Try to play the video
            videoRef.current.play().catch((err) => {
              console.error("Error playing fallback video:", err);
            });
          }
        })
        .catch((fallbackError) => {
          console.error("Fallback also failed:", fallbackError);
          console.error("Fallback error name:", fallbackError.name);
          console.error("Fallback error message:", fallbackError.message);
          alert(
            `Unable to access camera: ${fallbackError.message}. Please check your camera permissions and try refreshing the device list.`,
          );
        });
    }
  }, [selectedVideoDevice, selectedAudioDevice]);

  /* Stopping a recording is handled by `stopRecording` above; the recording
     session releases the capture resources once the recorder has flushed. */

  /* Point the loops at the painters from the latest render. Running on every
     render (no dependency list) is deliberate: the painters close over current
     props and state, and a loop that outlives a render must never keep calling
     a stale one. */
  useEffect(() => {
    drawMasterCanvasFrameRef.current = drawMasterCanvasFrame;
    drawWaveformFrameRef.current = drawWaveformFrame;
    userPianoHeightRef.current = userPianoHeight;
  });

  /* Tear everything down if the user navigates away mid-take. */
  useEffect(() => {
    const frameLoop = frameLoopRef.current;
    const waveformLoop = waveformLoopRef.current;
    const session = recordingSessionRef.current;

    return () => {
      frameLoop.stop();
      waveformLoop.stop();
      void session.stop();
    };
  }, []);

  return (
    <MidiProvider>
      {/* Captures timestamped MIDI note events while recording is active */}
      <MidiEventRecorder
        isRecording={recording}
        onNoteEvent={handleNoteEvent}
        startTimeRef={recordingStartTimeRef}
      />
      <div className="App">
        <div className="main-container">
          <div className="capture-column">
            <div className="video-container">
              <video
                ref={videoRef}
                autoPlay={true}
                playsInline={true}
                muted={true}
                width="540"
                height="675"
              ></video>

              <div className="piano" style={{ bottom: `${userPianoHeight}px` }}>
                <PianoCanvas
                  numOctaves={octaveRange}
                  videoWidth={540}
                  canvasRef={pianoRef}
                  pressedKeyColor={pressedKeyColor}
                  octaveOffset={octaveOffset}
                  chordsEnabled={chordsEnabled}
                  bubbleChords={bubbleChords}
                />
              </div>
            </div>

            <div className="recording">
              {/* ── Side-by-side media pickers ── */}
              <div className="media-zones">

              {/* Intro image picker */}
              <div className={`intro-image-zone${introImageFile ? " has-file" : ""}`}>
                {introImageFile ? (
                  <div className="intro-image-info">
                    <div className="intro-image-header">
                      {introImagePreviewUrl && (
                        <img
                          className="intro-image-thumb"
                          src={introImagePreviewUrl}
                          alt="intro preview"
                        />
                      )}
                      <div className="intro-image-details">
                        <span className="intro-image-name">🖼 {introImageFile.name}</span>
                        <span className="intro-image-hint">Fades out over first 1.5s</span>
                      </div>
                      <button
                        className="intro-image-remove"
                        onClick={() => setIntroImageFile(null)}
                        title="Remove intro image"
                      >✕</button>
                    </div>
                  </div>
                ) : (
                  <div className="intro-image-select-btn">
                    <div className="intro-image-select-text">
                      🖼 Add Intro Image
                      <span className="intro-image-dims">1080 × 1350 px</span>
                    </div>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (file) setIntroImageFile(file);
                        e.target.value = "";
                      }}
                    />
                  </div>
                )}
              </div>

              {/* Backing track picker */}
              <div className={`backing-track-zone${backingTrackFile ? " has-file" : ""}`}>
                {backingTrackFile ? (
                  <div className="backing-track-info">
                    <div className="backing-track-header">
                      <span className="backing-track-name">🎵 {backingTrackFile.name}</span>
                      <button
                        className="backing-track-remove"
                        onClick={() => setBackingTrackFile(null)}
                        title="Remove backing track"
                      >✕</button>
                    </div>
                    <canvas
                      ref={waveformCanvasRef}
                      className="waveform-canvas"
                      width={480}
                      height={36}
                    />
                    <div className="backing-track-volume">
                      <label htmlFor="backingVolume">Volume</label>
                      <input
                        id="backingVolume"
                        type="range"
                        min="0"
                        max="1"
                        step="0.01"
                        value={backingTrackVolume}
                        onChange={(e) => {
                          const vol = parseFloat(e.target.value);
                          setBackingTrackVolume(vol);
                          // Update gain node directly — no effect lag
                          if (backingTrackGainRef.current) {
                            backingTrackGainRef.current.gain.value = vol;
                          }
                        }}
                      />
                      <span>{Math.round(backingTrackVolume * 100)}%</span>
                    </div>
                  </div>
                ) : (
                  <div className="backing-track-select-btn">
                    🎵 Add Backing Track
                    <input
                      type="file"
                      accept="audio/*,.mp3"
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (file) setBackingTrackFile(file);
                        e.target.value = "";
                      }}
                    />
                  </div>
                )}
              </div>

              </div>{/* end .media-zones */}

              <button
                className={`btn ${
                  recording ? "btn-warning" : "btn-success"
                } btn-sm`}
                onClick={recording ? stopRecording : startRecording}
                disabled={converting}
              >
                {recording ? "Stop Recording" : "Start Recording"}
              </button>

              {!recording && recordedChunks.length > 0 && (
                <div className="download-options-section">
                  <p className="download-options-help">
                    Choose from one of the download options below
                  </p>
                  <div className="download-rows">
                    {/* Row 1: plain downloads */}
                    <div className="download-row">
                      <button
                        className="btn btn-primary btn-sm download-btn"
                        onClick={convertToMP4}
                        disabled={converting || !ffmpegLoaded || renderingEffects}
                      >
                        {converting
                          ? // 0 means ffmpeg could not measure this clip, so
                            // show activity without a misleading percentage.
                            conversionProgress > 0
                            ? `MP4… ${conversionProgress}%`
                            : "Converting…"
                          : !ffmpegLoaded
                            ? "Loading…"
                            : "Download as MP4"}
                      </button>

                      <button
                        className="btn btn-light btn-sm download-btn"
                        onClick={downloadWebM}
                        disabled={converting || renderingEffects}
                      >
                        Download as WebM
                      </button>
                    </div>

                    {/* Row 2: effects downloads */}
                    <div className="download-row">
                      <button
                        className="btn btn-sm download-btn download-btn--effects"
                        onClick={() => downloadWithEffects('mp4')}
                        disabled={converting || renderingEffects || !ffmpegLoaded}
                      >
                        {renderingEffects
                          ? `Rendering… ${effectsProgress}%`
                          : !ffmpegLoaded
                            ? "Loading…"
                            : "Download as MP4 (FX)"}
                      </button>

                      <button
                        className="btn btn-sm download-btn download-btn--effects"
                        onClick={() => downloadWithEffects('webm')}
                        disabled={converting || renderingEffects}
                      >
                        {renderingEffects
                          ? `Rendering… ${effectsProgress}%`
                          : "Download as WebM (FX)"}
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
          {hasMediaPermission && (
            <div className="settings">
              <h2 style={{ marginTop: 0 }}>Piano Settings</h2>
              <MidiStatusControl />
              <PianoPositionControl
                octaves={octaveRange as OctaveOptions}
                recording={recording}
                userPianoHeight={userPianoHeight}
                setUserPianoHeight={setUserPianoHeight}
                chordsEnabled={chordsEnabled}
              />
              <div>
                <OctaveOffsetButtons
                  offset={octaveOffset}
                  onIncrement={handleOctaveIncrement}
                  onDecrement={handleOctaveDecrement}
                  numOctaves={6}
                />
              </div>
              <ColorPicker onSelect={setPressedKeyColor} />

              <div className="settings-row">
                <label className="control-label" htmlFor="customCheck1">
                  Display Chords:
                </label>
                <div className="settings-row-controls">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    checked={chordsEnabled}
                    onChange={(e) => setChordsEnabled(e.target.checked)}
                    id="customCheck1"
                  />
                </div>
              </div>

              {chordsEnabled && (
                <div className="settings-row">
                  <label className="control-label" htmlFor="customCheck2">
                    Bubble Chords:
                  </label>
                  <div className="settings-row-controls">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      checked={bubbleChords}
                      onChange={(e) => setBubbleChords(e.target.checked)}
                      id="customCheck2"
                    />
                  </div>
                </div>
              )}

              {chordsEnabled && (
                <span> Chords will display under the piano </span>
              )}

              <OctaveRangeDropdown
                octaves={[3, 5, 6]}
                selectedOctave={octaveRange}
                onSelect={(octave) => setOctaveRange(octave as OctaveOptions)}
              ></OctaveRangeDropdown>

              <h2 className="settings-divider">Video / Audio Settings</h2>
              <VideoSourceDropdown
                videoDevices={videoDevices}
                selectedVideoDevice={selectedVideoDevice}
                onSelect={(deviceId) => setSelectedVideoDevice(deviceId)}
              />
              <AudioSourceDropdown
                audioDevices={audioDevices}
                selectedAudioDevice={selectedAudioDevice}
                onSelect={(deviceId) => setSelectedAudioDevice(deviceId)}
              />

              <div className="settings-guide-row">
                <button
                  className="btn-user-guide"
                  onClick={() => navigate("/about")}
                >
                  See User Guide
                </button>
              </div>
            </div>
          )}

          <canvas
            ref={masterCanvasRef}
            width={MASTER_CANVAS_WIDTH}
            height={MASTER_CANVAS_HEIGHT}
            style={{ display: "none" }} // Hide it from the user
          >
            {" "}
          </canvas>
        </div>
      </div>
    </MidiProvider>
  );
};

export default InstaKeys;
